/**
 * i18next bootstrap.
 *
 * Namespaces map 1:1 onto the lazy-loaded page modules in src/routes/index.jsx,
 * so a locale catalog arrives with the route chunk that needs it rather than in
 * one monolithic bundle. Add a namespace by dropping <ns>.json into every
 * directory under ./locales/ that is listed in AVAILABLE.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

import { AVAILABLE, DEFAULT_LOCALE, SUPPORTED_CODES } from './locales';

export const LANG_STORAGE_KEY = 'saf3ai.lang';

/**
 * Load a catalog, treating a missing file as an EMPTY catalog rather than an error.
 *
 * Namespaces are migrated locale by locale, so `de/hunt.json` legitimately may not exist yet.
 * A rejected dynamic import leaves i18next with no resources for that namespace and it then
 * renders raw keys — `sidebarSidebar.dashboard` instead of "Dashboard" — which reads as a
 * broken product. Resolving to `{}` lets `fallbackLng` do its job, so an untranslated namespace
 * degrades to readable English instead.
 */
const loadCatalog = async (lng, ns) => {
  try {
    return await import(`./locales/${lng}/${ns}.json`);
  } catch {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(`[i18n] no "${ns}" catalog for "${lng}" — falling back to ${DEFAULT_LOCALE}`);
    }
    return {};
  }
};

i18n
  .use(resourcesToBackend(loadCatalog))
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Only offer what actually has catalogs; anything else falls back to English.
    supportedLngs: AVAILABLE,
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: 'common',
    ns: ['common'],

    // Critical for the target set: without this i18next normalises pt-BR -> pt
    // and zh-Hant -> zh and then misses the catalogs entirely.
    load: 'currentOnly',
    nonExplicitSupportedLngs: false,

    // React escapes interpolated values already; double-escaping mangles
    // anything containing & or quotes.
    interpolation: { escapeValue: false },

    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ['localStorage'],
    },

    react: {
      // Page chunks suspend already; keeping this off avoids a second boundary.
      useSuspense: false,
    },

    // Surface missing keys loudly in dev, silently fall back in production.
    saveMissing: false,
    missingKeyHandler:
      process.env.NODE_ENV === 'development'
        ? (lngs, ns, key) => {
            // eslint-disable-next-line no-console
            console.warn(`[i18n] missing key "${key}" in ${ns} for ${lngs?.join(',')}`);
          }
        : undefined,
  });

/** All codes the platform intends to ship, for tooling/tests. */
export { SUPPORTED_CODES };

export default i18n;
